// Thin fetch wrapper around the backend API.

async function json(res) {
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    const err = new Error(body.error || `Request failed (${res.status})`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

const withAuth = (opts = {}) => ({ ...opts, credentials: "include" });

export const api = {
  previewScan(file, turnstileToken) {
    const fd = new FormData();
    fd.append("image", file);
    fd.append("turnstileToken", turnstileToken || "");
    return fetch("/api/receipts/preview", { method: "POST", body: fd }).then(
      json,
    );
  },

  sendContactMessage(name, email, message, turnstileToken) {
    return fetch("/api/contact", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ name, email, message, turnstileToken }),
    }).then(json);
  },

  sendLoginCode(email, turnstileToken) {
    return fetch(
      "/api/auth/send-code",
      withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, turnstileToken }),
      }),
    ).then(json);
  },

  verifyLoginCode(email, pin) {
    return fetch(
      "/api/auth/verify-code",
      withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, pin }),
      }),
    ).then(json);
  },

  authConfig() {
    return fetch("/api/auth/config", withAuth()).then(json);
  },

  logout() {
    return fetch("/api/auth/logout", withAuth({ method: "POST" })).then(json);
  },

  me() {
    return fetch("/api/auth/me", withAuth()).then(json);
  },

  scanReceipt(file, signal) {
    const fd = new FormData();
    fd.append("image", file);
    return fetch(
      "/api/receipts/scan",
      withAuth({ method: "POST", body: fd, signal }),
    ).then(json);
  },

  listExpenses(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return fetch(`/api/expenses${qs ? `?${qs}` : ""}`, withAuth()).then(json);
  },

  expensesTotal(params = {}) {
    const qs = new URLSearchParams(params).toString();
    return fetch(
      `/api/expenses/total${qs ? `?${qs}` : ""}`,
      withAuth(),
    ).then(json);
  },

  createExpense(data) {
    return fetch(
      "/api/expenses",
      withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    ).then(json);
  },

  updateExpense(id, data) {
    return fetch(
      `/api/expenses/${id}`,
      withAuth({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    ).then(json);
  },

  deleteExpense(id) {
    return fetch(`/api/expenses/${id}`, withAuth({ method: "DELETE" })).then(
      json,
    );
  },

  regenReceipt(id) {
    return fetch(
      `/api/expenses/${id}/regen-receipt`,
      withAuth({ method: "POST" }),
    ).then(json);
  },

  geocode(q) {
    return fetch(`/api/geocode?q=${encodeURIComponent(q)}`, withAuth()).then(
      json,
    );
  },

  reverseGeocode(lat, lon) {
    return fetch(`/api/geocode/reverse?lat=${lat}&lon=${lon}`, withAuth()).then(
      json,
    );
  },

  calcRoute(stops, returnTrip) {
    return fetch(
      "/api/distance",
      withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ stops, returnTrip }),
      }),
    ).then(json);
  },

  saveDrive(data) {
    return fetch(
      "/api/drives",
      withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    ).then(json);
  },

  health() {
    return fetch("/api/health", withAuth()).then(json);
  },

  reportUrl({ period, year, value, title, currency }) {
    const qs = new URLSearchParams({ period, year, value });
    if (title) qs.set("title", title);
    if (currency) qs.set("currency", currency);
    return `/api/reports?${qs.toString()}`;
  },

  listReports() {
    return fetch("/api/report-defs", withAuth()).then(json);
  },

  createReport(data) {
    return fetch(
      "/api/report-defs",
      withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    ).then(json);
  },

  updateReport(id, data) {
    return fetch(
      `/api/report-defs/${id}`,
      withAuth({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    ).then(json);
  },

  deleteReport(id) {
    return fetch(`/api/report-defs/${id}`, withAuth({ method: "DELETE" })).then(
      json,
    );
  },

  reportPdfUrl(id) {
    return `/api/report-defs/${id}/pdf`;
  },

  reportCsvUrl(id) {
    return `/api/report-defs/${id}/csv`;
  },

  archiveReport(id) {
    return fetch(
      `/api/report-defs/${id}/archive`,
      withAuth({ method: "POST" }),
    ).then(json);
  },

  unarchiveReport(id) {
    return fetch(
      `/api/report-defs/${id}/unarchive`,
      withAuth({ method: "POST" }),
    ).then(json);
  },

  submitReport(id, data) {
    return fetch(
      `/api/report-defs/${id}/submit`,
      withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    ).then(json);
  },

  getSettings() {
    return fetch("/api/settings", withAuth()).then(json);
  },

  saveSettings(data) {
    return fetch(
      "/api/settings",
      withAuth({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(data),
      }),
    ).then(json);
  },

  checkout(packId, currency) {
    return fetch(
      "/api/billing/checkout",
      withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ packId, currency }),
      }),
    ).then(json);
  },

  listTransactions() {
    return fetch("/api/billing/transactions", withAuth()).then(json);
  },

  scanHistory() {
    return fetch("/api/scans/history", withAuth()).then(json);
  },

  listCategories() {
    return fetch("/api/categories", withAuth()).then(json);
  },

  addCategory(name) {
    return fetch(
      "/api/categories",
      withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      }),
    ).then(json);
  },

  renameCategory(id, name) {
    return fetch(
      `/api/categories/${id}`,
      withAuth({
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name }),
      }),
    ).then(json);
  },

  deleteCategory(id) {
    return fetch(`/api/categories/${id}`, withAuth({ method: "DELETE" })).then(
      json,
    );
  },

  listMembers() {
    return fetch("/api/account/members", withAuth()).then(json);
  },

  inviteMember(email, message) {
    return fetch(
      "/api/account/members",
      withAuth({
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, message }),
      }),
    ).then(json);
  },

  removeMember(id) {
    return fetch(`/api/account/members/${id}`, withAuth({ method: "DELETE" })).then(
      json,
    );
  },

  deleteAccount() {
    return fetch("/api/account", withAuth({ method: "DELETE" })).then(json);
  },
};
